<template>
  <div class="grid-x grid-margin-x">
    <div
      v-if="isMobile"
      class="cell title-mobile"
      :class="isSpacingExperimentEnabled ? 'padding-bottom-xs' : 'padding-bottom-s'"
    >
      <ProductTitle />
      <ReviewRating v-if="isBcom" />
    </div>
    <div class="cell medium-6 large-6 xlarge-7">
      <Media />
    </div>
    <div class="cell medium-6 large-5 large-offset-1 xlarge-5 xlarge-offset-0">
      <div
        v-if="!isMobile"
        class="title margin-bottom-s"
      >
        <ProductTitle />
        <ReviewRating v-if="isBcom" />
      </div>
      <ColorSwatches
        v-if="showColorSwatches"
        :id="productId"
        :class="isSpacingExperimentEnabled ? 'margin-bottom-xs' : 'margin-bottom-m'"
        :color-props="colorProps"
      />
      <div v-if="isConsolidatedMasterBcom">
        <ProductPrice
          :id="productId"
          :product="product"
        />
      </div>
      <GiftCardForm />
      <ProsZoneAVertical
        v-if="!isMobile"
        data-testid="pros-section"
        class="margin-top-m"
      />
      <LazyLoadWhenInViewport>
        <SponsoredItemTop
          v-if="showSponsoredItemTop"
          :enable-click-tags-for-s-m-products="killswitches.enableClickTagsForSMProducts"
          :is-simplification-mcom-on="killswitches.priceSimplificationEnabled"
        />
      </LazyLoadWhenInViewport>
    </div>
    <div
      class="cell large-12"
      data-testid="product-details-section"
    >
      <ProductDetailsFacade
        :class="{'margin-top-xl': !isMobile && !showSponsoredItemTop}"
        data-testid="product-details-facade"
      />
    </div>
  </div>
</template>

<script setup>
import { defineAsyncComponent } from 'vue';
import { useInitColorPDP as useInitColor } from '@common-vue/media-card-large/src/modules/color';
import { useInitSizePDP } from '@common-vue/media-card-large/src/modules/size';
import { SponsoredItemTop } from '@common-vue/site-monetization';
import ProductTitle from '@components/ProductCard/ProductTitle.common';
import Media from '@components/common/Media/Media';
import { ProductDetailsFacade } from '@modules/productDetails';
import ReviewRating from '@components/ReviewRating';
import ProsZoneAVertical from '@components/pros/ProsZoneAVertical';
import ColorSwatches from '@components/ColorSwatches';
import { GiftCardForm } from '@modules/giftcards';
import { useColor } from '@modules/color/composables';
import { useBrand } from '@composables/useBrand';
import { useProductData } from '@composables/useProductData';
import { useProduct } from '@modules/product';
import { useFeatureEligibility, useProductFlags, useKillswitches } from '@modules/featureControl';
import { useProductType } from '@composables/useProductType';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useExperiments } from '@composables/useExperiments';
import LazyLoadWhenInViewport from '@components/common/LazyLoadWhenInViewport';

/**
 * * Lazy load ProductPrice component
 * @type {import('./ProductPrice.bcom.vue').default | import('./ProductPrice.mcom.vue').default}
 */
const ProductPrice = defineAsyncComponent(() => import(
  /* webpackMode: "eager" */
  /* webpackPreload: true */
  /* webpackChunkName: "PDP_ProductPrice" */
  `./ProductPrice.${process.env.ENTRY_POINT}`
));

const { isBcom } = useBrand();
const { isMobile } = useDeviceType();
const { killswitches } = useKillswitches();

const { productId } = useProduct();
const product = useProductData();
const { hasColorSwatchesFlag, hasColorsFlag } = useProductFlags();
useInitColor({ id: productId, product: product.value });
useInitSizePDP({ id: productId, product: product.value });

const { isConsolidatedMaster, isLatchKey } = useProductType({ productId });
const { colorProps } = useColor(productId);
const showColorSwatches = isBcom ? (hasColorsFlag || isLatchKey) : hasColorSwatchesFlag;
const isConsolidatedMasterBcom = isBcom && isConsolidatedMaster;
const {
  showSponsoredItemTop,
} = useFeatureEligibility();
const {
  experimentFlags: { isSpacingExperimentEnabled },
} = useExperiments();
</script>

<style lang="scss" brand="mcom" scoped>
@import "~settingSCSS";
@include title;
</style>

<style lang="scss" brand="bcom" scoped>
@import "~settingSCSS";
@include title;
</style>
